/**
 * apps/web :: hooks :: useFirstPassUnlocked
 *
 * Whole-app hook that resolves "has this learner passed at least one
 * exam?" so navigation surfaces (sidebar / header / dashboard cards)
 * can conditionally reveal the post-pass journey link (ROADS-431).
 *
 * Caching policy:
 *   - The result is memoised in a module-level promise the first time
 *     any component asks. Subsequent mounts await the same promise.
 *   - We do not auto-invalidate; the hook re-checks only after a forced
 *     reset via [resetFirstPassCache] (currently unused — exam submit
 *     refreshes the cache on success in a follow-up wave).
 *   - On error the cache is left empty so a later mount can retry.
 *
 * Why not React Query: the rest of the learner shell already builds on
 * a hand-rolled useEffect pattern (see `LearnerPortalShell`,
 * `DashboardPage`); introducing a new client just for one boolean
 * widens the change surface for this PR. A future ticket may migrate
 * the auth/profile hooks to TanStack Query in one shot.
 */
import { useEffect, useState } from "react";

import { getProfile } from "@/lib/profile-api";
import { checkFirstPassGate } from "@/lib/whatsnext-gate";

export interface FirstPassState {
  /** true while the first request is in flight. */
  readonly loading: boolean;
  /** true iff `checkFirstPassGate` returned `unlocked`. */
  readonly unlocked: boolean;
}

let cachedPromise: Promise<boolean> | null = null;

/** Test/escape hatch: clear the module-level cache. */
export function resetFirstPassCache(): void {
  cachedPromise = null;
}

async function resolveOnce(): Promise<boolean> {
  const profile = await getProfile();
  if (!profile) return false;
  const status = await checkFirstPassGate(profile.target_states);
  return status.kind === "unlocked";
}

export default function useFirstPassUnlocked(): FirstPassState {
  const [state, setState] = useState<FirstPassState>({
    loading: true,
    unlocked: false,
  });

  useEffect(() => {
    let cancelled = false;
    if (cachedPromise == null) {
      cachedPromise = resolveOnce().catch((err) => {
        // Drop the cache on error so a later mount can retry.
        cachedPromise = null;
        throw err;
      });
    }
    cachedPromise
      .then((unlocked) => {
        if (!cancelled) setState({ loading: false, unlocked });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, unlocked: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
